import axios from "./request";
import { toast } from "react-toastify";
import { API_PATH } from "../utils/config";

export const getUserProfileById = (user_id) => {
  return axios.get(API_PATH.GET_USER_PROFILE_BY_ID(user_id));
};

export const getUserWheels = (user_id, query) => {
  return axios.get(API_PATH.GET_USER_WHEELS_BY_ID(user_id, query));
};

export const getUserWheelsV2 = (query) => {
  return axios.get(API_PATH.GET_USER_WHEELS_BY_ID_V2(query));
};

// QA-1036 — the wheel pickers (Capture "Add to existing Wheel" + Chat "Share a
// Wheel") could only ever reach the FIRST `page_size=40` page: they fetched
// getUserWheels(userId) once and searched/sorted that page client-side, so a
// user with >40 wheels (e.g. count=131) could file a capture to only ~30% of
// them and could not even search for the rest (the search box never queried the
// API). This pages through EVERY DRF page (following `next`) and returns the
// merged list, so the pickers' existing client-side search/sort span the whole
// set. `maxPages` is a safety valve: a backend that never clears `next` (or a
// runaway count) can never spin this loop forever.
//
// ISP-9999 — relying on `next` alone left the Capture "Attach to Wheels"
// picker stuck on "Loading your wheels…" indefinitely for gordon-21
// (count=149): a `next` field that never clears on some pages made this loop
// keep firing sequential requests all the way to `maxPages` (200) before the
// query ever resolved, even though every wheel was already in hand after the
// 4th page. We now ALSO stop as soon as we've collected `count` wheels — the
// number the very first response already told us — so a flaky `next` can no
// longer force ~200 wasted round-trips before the picker can render data it
// already has.
// `extraQuery` is opt-in and additive to the `&page=` param below — e.g. chat's
// Share-a-Wheel picker passes `&include_collaborations=true` so the list also
// includes wheels the user collaborates on (not just owns). Every other caller
// (Capture's AttachWheelModal) omits it and gets byte-identical behavior.
export const getAllUserWheels = async (
  user_id,
  { maxPages = 200, extraQuery = "" } = {}
) => {
  let results = [];
  let count = 0;
  for (let page = 1; page <= maxPages; page += 1) {
    // Reuse the single-page endpoint (keeps one source of truth for the URL +
    // request interceptors). `page_size=40` is baked into the path; append page.
    const res = await getUserWheels(user_id, `&page=${page}${extraQuery}`);
    const d = res?.data ?? {};
    const pageResults = Array.isArray(d) ? d : d.results ?? d.data ?? [];
    results = results.concat(Array.isArray(pageResults) ? pageResults : []);
    if (typeof d.count === "number") count = d.count;
    if (typeof d.count === "number" && results.length >= d.count) break;
    // DRF paginator returns an absolute `next` URL while more pages remain and
    // null on the last page. A non-paginated (bare-array) response has no
    // `next`, so we stop after one request — unchanged from the old behaviour.
    if (!d || !d.next) break;
  }
  return { data: { results, count } };
};

export const fetchWheels = async ({ pageParam = 1 }) => {
  const response = await axios.get("/api/v2/wheel/", {
    params: {
      page: pageParam, // Current page to fetch
      page_size: 20, // Number of results per page
      user_id: 8,
    },
  });
  return response.data;
};

export const getBgWallpapers = () => {
  return axios.get(API_PATH.GET_BG_WALLPAPERS);
};

export const getPrimaryWheel = (userId) => {
  return axios.get(API_PATH.GET_PRIMARY_WHEEL);
};

export const createUserWheel = (data) => {
  return axios.post(API_PATH.CREATE_WHEEL, data);
};

export const getAiWheel = (temp_id) => {
  return axios.get(API_PATH.GET_LATEST_AI_WHEEL(temp_id));
};

export const generateAiWheel = (data) => {
  return axios.post(API_PATH.GENERATE_AI_WHEEL, data);
};

// AI Wheel Builder staged funnel (D14). Both endpoints 404 unless the server
// flag AI_FUNNEL_ENABLED is on; callers must gate the UI on REACT_APP_AI_FUNNEL.
export const aiFunnelScrapeWebsite = (url) => {
  return axios.post(API_PATH.AI_FUNNEL_SCRAPE, { url });
};

// Y7: DEAD until the Segments & Cost review stage (deck 02c/02f) is built — no
// caller invokes this yet. Keep it wired for that stage; do not treat its
// presence as evidence the cost-estimate UI exists.
export const aiFunnelEstimateCost = (data) => {
  return axios.post(API_PATH.AI_FUNNEL_ESTIMATE, data);
};

// AI Wheel Copilot (D13): advice + proposed edits for the open Wheel.
// Server-authoritative: tier gate + AI Wheel Token billing + op whitelist all
// happen on the backend; this just carries {wheel_state, ask}.
export const askWheelCopilot = (data) => {
  return axios.post(API_PATH.WHEEL_COPILOT, data);
};

export const getMyCustomColors = () => {
  return axios.get(API_PATH.GET_MY_CUSTOM_COLORS());
};

export const addMyCustomColor = (data) => {
  return axios.post(API_PATH.ADD_MY_CUSTOM_COLORS(), data);
};

export const editMyCustomColor = (id, data) => {
  return axios.patch(API_PATH.EDIT_MY_CUSTOM_COLORS(id), data);
};

export const deleteMyCustomColor = (id) => {
  return axios.delete(API_PATH.EDIT_MY_CUSTOM_COLORS(id));
};
export const duplicateWheel = (data) => {
  return axios.post(API_PATH.CLONE_WHEEL(data.WheelID), data.data);
};
export const search = (text) => {
  return axios.get(API_PATH.GENERAL_SEARCH(text));
};

export const searchUsers = (search, page = 1, page_size = 10) => {
  return axios.get(API_PATH.SEARCH_USERS(search, page, page_size));
};

export const searchWheels = (search, page = 1, page_size = 10) => {
  return axios.get(API_PATH.SEARCH_WHEELS(search, page, page_size));
};

// #920 §5 — collections (renamed folders) for the global search's Collections
// tab. Returns the signed-in user's collections; SearchBar filters them by name
// through FoldersV2/collectionSearch.js. See API_PATH.SEARCH_COLLECTIONS.
export const searchUserCollections = (search, userId, page = 1, page_size = 50) => {
  return axios.get(
    API_PATH.SEARCH_COLLECTIONS({ user: userId, search, page, page_size })
  );
};

export const cloneWheel = (id, data) => {
  return axios.post(API_PATH.CLONE_WHEEL(id), data);
};

export const cloneSegment = (id) => {
  return axios.post(API_PATH.CLONE_SEGMENT(id));
};

// SEGMENT COLLECT §1 — the current user's collected segments (each rendered as a
// hollow-except-the-collected-segment Wheel in the Segment Library "Segment
// Sets" tab — renamed from "Collections" by #920a; the ROUTE is unchanged).
export const getSegmentCollections = (page = 1, page_size = 12) => {
  return axios.get(API_PATH.SEGMENT_COLLECTIONS(page, page_size));
};

export const likeUnlikeWheel = (id) => {
  return axios.post(API_PATH.LIKE_UNLIKE_WHEEL(id));
};

export const getAllTransactions = (
  startDate,
  endDate,
  limit = 10,
  offset = 0
) => {
  return axios.get(
    API_PATH.GET_ALL_TRANSACTIONS(startDate, endDate, limit, offset)
  );
};

export const getAllCards = () => {
  return axios.get(API_PATH.LIST_ALL_CARDS);
};

export const removeCard = (id) => {
  return axios.post(API_PATH.DELETE_CARD(id));
};

export const getCurrentPlan = () => {
  return axios.get(API_PATH.GET_ACTIVE_SUBSCRIPTION);
};

export const canCreateWheel = () => {
  return axios.get(API_PATH.CAN_CREATE_WHEEL);
};

export const canCloneWheel = () => {
  return axios.get(API_PATH.CAN_CLONE_WHEEL);
};

export const getSubscriptionComparison = (data) => {
  return axios.get(API_PATH.COMPARE_SUBSCRIPTION, { params: data });
};

export const downloadInvoice = (data) => {
  return axios.post(API_PATH.DOWNLOAD_INVOICE, data);
};

// Referrals

export const getAmbassadorReferral = (queryParams) => {
  const { sub_filter, ...otherParams } = queryParams;

  // Build query string manually for sub_filter to avoid array notation
  let queryString = "";
  const searchParams = new URLSearchParams();

  // Add other parameters
  Object.entries(otherParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });

  // Add sub_filter parameters individually
  if (sub_filter && Array.isArray(sub_filter)) {
    sub_filter.forEach((filter) => {
      searchParams.append("sub_filter", filter);
    });
  }

  queryString = searchParams.toString();
  const url = queryString
    ? `${API_PATH.REFERRAL_AMBASSADOR}?${queryString}`
    : API_PATH.REFERRAL_AMBASSADOR;

  return axios.get(url);
};

export const getBtReferral = (queryParams) => {
  const { sub_filter, ...otherParams } = queryParams;

  // Build query string manually for sub_filter to avoid array notation
  let queryString = "";
  const searchParams = new URLSearchParams();

  // Add other parameters
  Object.entries(otherParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });

  // Add sub_filter parameters individually
  if (sub_filter && Array.isArray(sub_filter)) {
    sub_filter.forEach((filter) => {
      searchParams.append("sub_filter", filter);
    });
  }

  queryString = searchParams.toString();
  const url = queryString
    ? `${API_PATH.REFERRAL_BT}?${queryString}`
    : API_PATH.REFERRAL_BT;

  return axios.get(url);
};

export const getDirectReferral = (queryParams) => {
  const { sub_filter, ...otherParams } = queryParams;

  // Build query string manually for sub_filter to avoid array notation
  let queryString = "";
  const searchParams = new URLSearchParams();

  // Add other parameters
  Object.entries(otherParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });

  // Add sub_filter parameters individually
  if (sub_filter && Array.isArray(sub_filter)) {
    sub_filter.forEach((filter) => {
      searchParams.append("sub_filter", filter);
    });
  }

  queryString = searchParams.toString();
  const url = queryString
    ? `${API_PATH.REFERRAL_DIRECT}?${queryString}`
    : API_PATH.REFERRAL_DIRECT;

  return axios.get(url);
};

export const getAnalytics = (queryParams) => {
  const params = {
    ...queryParams,
    limit: 10,
  };
  return axios.get(API_PATH.ANALYTICS, { params });
};

export const sendReferralInvites = (data) => {
  return axios.post(API_PATH.REFERRAL_INVITES_API(), data);
};

export const getBalance = () => {
  return axios.get(API_PATH.GET_BALANCE);
};

export const getTokenBundles = () => {
  return axios.get(API_PATH.GET_TOKEN_BUNDLES);
};

export const purchaseTokenBundle = (data) => {
  return axios.post(API_PATH.PURCHASE_TOKEN_BUNDLE, data);
};

export const connectStripeAccount = (data) => {
  return axios.post(API_PATH.CONNECT_STRIPE_API(), data);
};

// Owner-only, idempotent backstop that (re)syncs the caller's own current Stripe
// subscription. Called after the subscription-success redirect so activation
// never depends solely on Stripe webhook delivery.
export const syncSubscription = () => {
  return axios.post(API_PATH.SYNC_SUBSCRIPTION, {});
};

export const getStripeAccountStatus = () => {
  return axios.get(API_PATH.GET_STRIPE_ACCOUNT_STATUS);
};

export const getReferralsChart = (category, time) => {
  return axios.get(API_PATH.REFERRAL_CHART(category, time));
};

export const downloadReferralsReport = (query) => {
  return axios.get(API_PATH.DOWNLOAD_REFERRALS_REPORT(query));
};

export const getHallOfFame = (query, startDate, endDate) => {
  return axios.get(API_PATH.GET_HALL_OF_FAME(query, startDate, endDate));
};

export const getFollowersGained = (query, startDate, endDate) => {
  return axios.get(API_PATH.GET_FOLLOWERS_GAINED(query, startDate, endDate));
};

export const getAnalyticsDetail = (queryParams) => {
  const params = {
    ...queryParams,
    limit: 10,
  };
  return axios.get(API_PATH.GET_ANALYTICS_DETAIL, { params });
};

export const getWheelEmailPermission = (id) => {
  return axios.get(API_PATH.GET_WHEEL_EMAIL_PERMISSION(id));
};

export const deleteWheelEmailPermission = (id, data) => {
  return axios.post(API_PATH.DELETE_WHEEL_EMAIL_PERMISSION(id), data);
};

export const uploadImage = (data) => {
  return axios.put(API_PATH.UPLOAD_IMAGE, data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getPrimaryWheelV2 = (id) => {
  return axios.get(API_PATH.GET_PRIMARY_WHEEL_V2(id));
};

export const getPrimaryOrTopWheelV2 = (id) => {
  return axios.get(API_PATH.GET_PRIMARY_OR_TOP_WHEEL_V2(id));
};

export const getQueriedUserWheels = (user_id, query) => {
  return axios.get(API_PATH.GET_USER_WHEELS(user_id, query));
};

export const signupBeta = (data) => {
  return axios.post(API_PATH.SIGNUP_BETA_V2(), data);
};

export const avifToPng = (file) => {
  const formData = new FormData();
  formData.append("avif_file", file);

  return axios.post(API_PATH.AVIF_T0_PNG, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Fetch Palette Library
export const getPaletteLibrary = (wheelId, type, search, usageType) => {
  return axios.get(
    API_PATH.GET_PALETTE_LIBRARY(wheelId, type, search, usageType)
  );
};

// Delete a Palette
export const deletePalette = (paletteId) => {
  return axios.delete(API_PATH.DELETE_PALETTE(), {
    data: { id: paletteId }, // Send ID in the request body
  });
};

// Generate a Scheme
// review-0921-fe-5955-followups NB-1 (staging port of dev PR #5957): bounded
// so a hung request can't leak a paletteInFlight begin() forever (see
// hooks/paletteInFlight.js's watchdog, the second line of defense for
// whatever a timeout doesn't catch).
export const generateScheme = (data) => {
  return axios.post(API_PATH.GENERATE_SCHEME(), data, { timeout: 20000 });
};

// Save a Palette
export const savePalette = (paletteData) => {
  return axios.post(API_PATH.SAVE_PALETTE(), paletteData, { timeout: 20000 });
};

// ── Theme library (SavedTheme) ─────────────────────────────────────────────
export const getThemeLibrary = (type, search) => {
  return axios.get(API_PATH.GET_THEME_LIBRARY(type, search));
};

export const saveTheme = (themeData) => {
  return axios.post(API_PATH.SAVE_THEME(), themeData);
};

export const renameTheme = (data) => {
  return axios.post(API_PATH.RENAME_THEME, data);
};

export const deleteTheme = (themeId) => {
  return axios.delete(API_PATH.DELETE_THEME(), { data: { id: themeId } });
};

// Browse ALL saved color palettes across every Wheel (no wheel scope). Reuses
// the existing palette-library endpoint: passing an empty wheel_id makes the
// backend skip its "if ... and wheel_id" filter, so no backend change needed.
export const getAllColorPalettes = (type, search, usageType = 2) => {
  return axios.get(API_PATH.GET_PALETTE_LIBRARY("", type, search, usageType));
};

// Generate Colors from Image
export const generateColorsFromImage = (imageUrl, count = 5) => {
  return axios.post(API_PATH.GENERATE_COLORS_FROM_IMAGE(), {
    image_url: imageUrl,
    count,
  });
};

export const generateColorsFromPrompt = (data) => {
  return axios.post(API_PATH.GENERATE_COLORS_FROM_PROMPT(), data);
};

export const saveRecentMedia = async (wheelId, mediaData) => {
  const response = await axios.post(API_PATH.SAVE_RECENT_MEDIA(wheelId), {
    wheel: wheelId,
    ...mediaData,
  });
  // The backend dedupes recent media by (user, wheel, url). When this media is
  // already saved to the wheel it returns already_exists=true instead of
  // creating a duplicate row. Let the user know instead of silently proceeding.
  // Only relevant when adding to an actual wheel (wheelId present).
  if (wheelId && response?.data?.data?.already_exists) {
    toast.warn("It's already been added to that wheel.");
  }
  return response;
};

export const saveExternalImage = (data) => {
  return axios.post(API_PATH.SAVE_EXTERNAL_IMAGE(), data);
};

// EMBED_URL_REHOST: fetch + moderate + re-host a pasted external image URL
// server-side; returns { url } (the S3 url) on pass, or a 400 with
// { data: { flagged: true }, message } when moderation rejects the image.
export const rehostEmbedImage = (data) => {
  return axios.post(API_PATH.EMBED_URL_REHOST(), data);
};

// Get Recent Media
export const getRecentMedia = (wheelId, type, page) => {
  return axios.get(API_PATH.GET_RECENT_MEDIA(wheelId, type, page));
};

// Get total storage
export const getTotalStorage = () => {
  return axios.get(API_PATH.GET_TOTAL_STORAGE());
};

// Get v2 storage usage (backend #1121). May serve zeros until counting is on.
export const getStorageUsage = () => {
  return axios.get(API_PATH.GET_STORAGE_USAGE());
};

// Get all storage
export const getTotalMedia = () => {
  return axios.get(API_PATH.GET_TOTAL_MEDIA());
};

// Delete selected media FILES by their S3 storage paths.
// The `api/media/delete/` endpoint has two branches keyed off the request body:
//   { paths: [<s3 key strings>] }   -> ownership-checked (has_delete_permission) file delete
//   { wheel_ids: [<int>] }          -> whole-wheel delete (see deleteWheelsFromStorage)
// This function is the FILE branch, so it must send `paths` (strings). It was
// previously sending `{ wheel_ids: paths }`, which the BE serializer rejected
// (wheel_ids is an IntegerField) with 400 "A valid integer is required" — the
// delete silently failed. (ISP media-library delete fix.)
export const deleteMediaFiles = (paths) => {
  return axios.post(API_PATH.DELETE_MEDIA(), {
    paths,
  });
};

// Rename one Media Library file. `path` is its existing S3 storage path (the
// same identifier deleteMediaFiles above uses) and `name` is the new display
// name — the S3 key/URL is never touched (gordon-0831-media-library-rename).
export const renameMedia = (path, name) => {
  return axios.post(API_PATH.RENAME_MEDIA(), {
    path,
    name,
  });
};

// WL-072: which of the caller's own Wheels (id, name, thumbnail) reference
// each of the given storage paths. Powers the delete modal's Wheel checklist.
// Inert (`{tracked:false, wheels:{}}`) while the BE's MEDIA_LIFECYCLE_SAFE_DELETE
// flag is off — callers must treat `tracked:false` as "unknown", not "unused".
export const getMediaWheels = (paths) => {
  return axios.post(API_PATH.GET_MEDIA_WHEELS(), {
    paths,
  });
};

// WL-072: detach one media file from a caller-selected SET of Wheels — the
// "remove from selected Wheels" alternative to a permanent Library delete.
// Does NOT delete the file itself (unlike deleteMediaFiles); the S3 object and
// the Library entry are untouched, only those Wheels' references are removed.
export const removeMediaFromWheels = (path, wheelIds) => {
  return axios.post(API_PATH.REMOVE_MEDIA_FROM_WHEELS(), {
    path,
    wheel_ids: wheelIds,
  });
};

// Delete whole WHEELS (and their associated media) from Storage by wheel id.
// Hits the same endpoint's wheel branch: the BE auto-sets delete_wheels=True
// whenever `wheel_ids` is present. Kept separate from deleteMediaFiles so the
// two request shapes (int ids vs s3 path strings) never get conflated — routing
// wheel ids through the `paths` branch would 404 (s3 head_object on an int key).
export const deleteWheelsFromStorage = (wheelIds) => {
  return axios.post(API_PATH.DELETE_MEDIA(), {
    wheel_ids: wheelIds,
  });
};

//delete all media
export const deleteAllMedia = () => {
  return axios.post(API_PATH.DELETE_ALL_MEDIA());
};

//get media by type
export const getMediaByCategory = (type, pageToken) => {
  return axios.get(API_PATH.GET_MEDIA_BY_CATEGORY(type, pageToken));
};
export const getContentModerationListing = (status, search, pageToken) => {
  return axios.get(
    API_PATH.GET_CONTENT_MODERATION_LISTING(status, search, pageToken)
  );
};

// ── MOD-1 staff Moderation Console (flag-gated; BE staff-only + 404 while OFF) ──
export const getModerationQueue = (params) =>
  axios.get(API_PATH.MODERATION_QUEUE(params));
export const getModerationStats = () => axios.get(API_PATH.MODERATION_STATS());
export const getModerationFlag = (id) =>
  axios.get(API_PATH.MODERATION_DETAIL(id));
export const approveModerationFlag = (id, note) =>
  axios.post(API_PATH.MODERATION_APPROVE(id), { note });
export const rejectModerationFlag = (id, note) =>
  axios.post(API_PATH.MODERATION_REJECT(id), { note });
export const reinstateModerationFlag = (id, note) =>
  axios.post(API_PATH.MODERATION_REINSTATE(id), { note });
export const resolveModerationAppeal = (id, decision, note) =>
  axios.post(API_PATH.MODERATION_APPEAL_RESOLVE(id), { decision, note });

export const getWheelMedia = (id) => {
  return axios.get(API_PATH.GET_WHEEL_MEDIA(id));
};

export const getWheelDetails = async (wheelId, queryParams = "") => {
  try {
    return await axios.get(
      `${API_PATH.GET_WHEEL_DETIALS(wheelId)}${queryParams}`
    );
  } catch (err) {
    throw err;
  }
};
//get notifications
export const getNotifications = (page) => {
  return axios.get(API_PATH.GET_NOTIFICATIONS(page));
};

//mark as reak
export const markAllRead = () => {
  return axios.post(API_PATH.MARK_ALL_READ());
};

// Mark a SINGLE notification read (P5S1-O12: clicking a row now persists its
// read state instead of leaving it forever unread).
export const markNotificationRead = (id) => {
  return axios.post(API_PATH.MARK_NOTIFICATION_READ(id));
};

// Server-authoritative unread notification total (not page-limited).
// Response: { data: { unread_count: <int> } }
export const getUnreadNotificationCount = () => {
  return axios.get(API_PATH.NOTIFICATION_UNREAD_COUNT());
};

// QA-1014: delete a single notification (FB-style kebab Delete). Hits the DRF
// ModelViewSet's default destroy route (DELETE /api/v1/notification/{id}/) which
// already exists — no new BE endpoint. Returns 204 on success.
export const deleteNotification = (id) => {
  return axios.delete(API_PATH.DELETE_NOTIFICATION(id));
};

export const checkIfUserEligible = (feature) => {
  return axios.post(API_PATH.CHECK_PERMISSIONS, { feature });
};

export const updateWheelPreview = async (wheelId) => {
  const response = await axios.put(
    API_PATH.TAKE_SCREENSHOT_OF_WHEEL(wheelId),
    {}
  );
  return response.data;
};

export const getCommissionStructure = () => {
  return axios.get(API_PATH.GET_COMMISSION_STRUCTURE);
};

export const getCommissionEvaluation = () => {
  return axios.get(API_PATH.GET_COMMISSION_EVALUATION);
};

export const sendWheelMedia = (wheelId, mediaData) => {
  return axios.post(API_PATH.SEND_WHEEL_MEDIA(wheelId), {
    wheel_id: wheelId,
    urls: mediaData,
  });
};

export const deleteWheelMedia = (data) => {
  return axios.post(API_PATH.DELETE_WHEEL_MEDIA(), data);
};

export const postMediaModeration = (data) => {
  return axios.post(API_PATH.POST_MEDIA_MODERATION(), data);
};

export const getProfileByReferCode = (code) => {
  return axios.get(API_PATH.GET_PROFILE_BY_REFER_CODE(code));
};

export const getLatestWheelToShowInReferModal = (id) => {
  return axios.get(API_PATH.GET_LATEST_WHEEL_TO_SHOW_IN_REFER_MODAL(id));
};

export const postWheelScreenshot = (wheelId, data) => {
  return axios.post(API_PATH.POST_SCREENSHOT(wheelId), data);
};

export const takeWheelScreenshot = (wheelId) => {
  return axios.post(API_PATH.TAKE_WHEEL_SCREENSHOT(wheelId), {});
};

export const getSegmentDetails = (segmentId) => {
  return axios.get(API_PATH.GET_SEGMENT_DETAILS(segmentId));
};

export const checkReferral = (data) => {
  return axios.post(API_PATH.CHECK_REFERRAL(), data);
};

export const renameColorPalette = (data) => {
  return axios.post(API_PATH.RENAME_COLOR_PALETTE, data);
};

export const getWheelCount = () => {
  return axios.get(API_PATH.GET_WHEEL_COUNT);
};

export const generateAICompletion = (data) => {
  return axios.post(API_PATH.GENERATE_AI_COMPLETION, data);
};

export const generateAISuggestions = (data) => {
  return axios.post(API_PATH.GENERATE_AI_SUGGESTIONS, data);
};

export const generateAITitles = (prompt, count = 1) => {
  const params = new URLSearchParams({
    prompt: prompt,
    count: count.toString(),
  });
  return axios.get(`${API_PATH.GENERATE_AI_TITLES}?${params}`);
};
